/**
 * @file recurrenceEngine.js
 * @description Pure-function recurrence engine for Todoist-style repeatable tasks.
 *
 * Core design: Single-Entity Mutation — a recurring task is ONE Firestore document
 * whose dueDate advances forward upon completion. No row cloning.
 *
 * Two anchor modes:
 *   "every"  → scheduled-date anchor with overdue fast-forwarding
 *   "every!" → completion-date anchor (next = completionDate + interval)
 */

const DAY_MAP = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
const WEEKDAYS = ["MO", "TU", "WE", "TH", "FR"];

/**
 * Parses an ISO "YYYY-MM-DD" string into a local Date at midnight.
 * @param {string} str
 * @returns {Date}
 */
function parseDate(str) {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Formats a Date as "YYYY-MM-DD".
 * @param {Date} date
 * @returns {string}
 */
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Returns the last valid day of a given month/year.
 * @param {number} year
 * @param {number} month - 0-indexed
 * @returns {number}
 */
function lastDayOfMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Adds N months to a date, clamping the day to the last valid day
 * of the target month (handles 31st→30th, Feb 29→28, etc.).
 * @param {Date} base
 * @param {number} months
 * @param {number} targetDay - the desired day-of-month
 * @returns {Date}
 */
function addMonthsClamped(base, months, targetDay) {
  let newMonth = base.getMonth() + months;
  let newYear = base.getFullYear() + Math.floor(newMonth / 12);
  newMonth = ((newMonth % 12) + 12) % 12;
  const maxDay = lastDayOfMonth(newYear, newMonth);
  const clampedDay = Math.min(targetDay, maxDay);
  return new Date(newYear, newMonth, clampedDay);
}

/**
 * Finds the next date in a set of weekdays after the given base date.
 * For interval > 1, advances by (interval - 1) full weeks after exhausting
 * the current week's remaining days in the set.
 * @param {Date} base
 * @param {string[]} byDay - e.g. ["MO", "WE", "FR"]
 * @param {number} interval - week interval (1 = every week)
 * @returns {Date}
 */
function getNextWeekdayInSet(base, byDay, interval) {
  const dayNums = byDay.map((d) => DAY_MAP[d]).sort((a, b) => a - b);
  const currentDayOfWeek = base.getDay();

  const nextInWeek = dayNums.find((d) => d > currentDayOfWeek);

  if (nextInWeek !== undefined) {
    const daysAhead = nextInWeek - currentDayOfWeek;
    const result = new Date(base);
    result.setDate(result.getDate() + daysAhead);
    return result;
  }

  const firstDayNextCycle = dayNums[0];
  const daysUntilSunday = 7 - currentDayOfWeek;
  const extraWeeks = (interval - 1) * 7;
  const daysAhead = daysUntilSunday + extraWeeks + firstDayNextCycle;
  const result = new Date(base);
  result.setDate(result.getDate() + daysAhead);
  return result;
}

/**
 * Finds the next date matching one of the target month-days after the anchor.
 * Handles month-end clamping (e.g., 31st in a 30-day month → 30th).
 * @param {Date} base
 * @param {number} interval - month interval
 * @param {number[]} monthDays - sorted array of target days, e.g. [1, 15]
 * @returns {Date}
 */
function getNextMonthDay(base, interval, monthDays) {
  const sortedDays = [...monthDays].sort((a, b) => a - b);
  const currentDay = base.getDate();

  const nextDay = sortedDays.find((d) => d > currentDay);
  if (nextDay !== undefined) {
    const maxDay = lastDayOfMonth(base.getFullYear(), base.getMonth());
    const clamped = Math.min(nextDay, maxDay);
    if (clamped > currentDay) {
      return new Date(base.getFullYear(), base.getMonth(), clamped);
    }
  }

  return addMonthsClamped(base, interval, sortedDays[0]);
}

/**
 * Computes the raw next candidate date from a base date, stepping forward
 * by one interval according to the recurrence rule.
 * @param {Date} baseDate
 * @param {Object} rule
 * @returns {Date}
 */
function computeRawStep(baseDate, rule) {
  const { frequency, interval = 1, byDay, byMonthDay } = rule;

  switch (frequency) {
    case "daily":
      return new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate() + interval
      );

    case "weekly":
      if (byDay && byDay.length > 0) {
        return getNextWeekdayInSet(baseDate, byDay, interval);
      }
      return new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate() + 7 * interval
      );

    case "monthly":
      if (byMonthDay && byMonthDay.length > 0) {
        return getNextMonthDay(baseDate, interval, byMonthDay);
      }
      return addMonthsClamped(baseDate, interval, baseDate.getDate());

    case "yearly": {
      const nextYear = baseDate.getFullYear() + interval;
      const m = baseDate.getMonth();
      const maxDay = lastDayOfMonth(nextYear, m);
      const clampedDay = Math.min(baseDate.getDate(), maxDay);
      return new Date(nextYear, m, clampedDay);
    }

    default:
      return new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate() + 1
      );
  }
}

/**
 * Calculates the next due date for a recurring task upon completion.
 *
 * - "every" mode: anchors on the original scheduled due date and fast-forwards
 *   past today if overdue.
 * - "every!" mode: anchors on the completion date (today).
 *
 * @param {string} currentDueDateStr - ISO "YYYY-MM-DD" of the current due date
 * @param {string} completionDateStr - ISO "YYYY-MM-DD" of when the task was completed
 * @param {Object} rule - RecurrenceRule object
 * @returns {string|null} Next due date as "YYYY-MM-DD", or null if recurrence expired
 */
export function calculateNextDueDate(currentDueDateStr, completionDateStr, rule) {
  if (!rule || !rule.frequency) return null;

  const completionDate = parseDate(completionDateStr);
  const mode = rule.mode || "every";

  let anchor;
  if (mode === "every!") {
    anchor = completionDate;
  } else {
    anchor = parseDate(currentDueDateStr);
  }

  let candidate = computeRawStep(anchor, rule);

  if (mode === "every") {
    while (candidate <= completionDate) {
      candidate = computeRawStep(candidate, rule);
    }
  }

  if (rule.endDate) {
    const endD = parseDate(rule.endDate);
    if (candidate > endD) {
      return null;
    }
  }

  return formatDate(candidate);
}

/**
 * Builds a RecurrenceRule object from structured picker form values.
 * @param {Object} formValues
 * @param {string} formValues.mode - "every" | "every!"
 * @param {string} formValues.frequency - "daily"|"weekly"|"monthly"|"yearly"
 * @param {number} formValues.interval
 * @param {string[]} [formValues.byDay]
 * @param {number[]} [formValues.byMonthDay]
 * @param {string} [formValues.endType] - "never" | "on_date" | "after_occurrences"
 * @param {string} [formValues.endDate] - ISO "YYYY-MM-DD"
 * @param {number} [formValues.maxOccurrences] - integer >= 1
 * @param {number} [formValues.occurrenceCount] - current occurrence (default 1)
 * @returns {Object} RecurrenceRule
 */
export function buildRecurrenceRule(formValues) {
  const endType = formValues.endType || (formValues.endDate ? "on_date" : formValues.maxOccurrences ? "after_occurrences" : "never");

  const rule = {
    isRecurring: true,
    mode: formValues.mode || "every",
    frequency: formValues.frequency || "daily",
    interval: formValues.interval || 1,
    endType,
    resetSubtasks: true,
    rawText: "",
  };

  if (
    formValues.frequency === "weekly" &&
    formValues.byDay &&
    formValues.byDay.length > 0
  ) {
    rule.byDay = formValues.byDay;
  }

  if (
    formValues.frequency === "monthly" &&
    formValues.byMonthDay &&
    formValues.byMonthDay.length > 0
  ) {
    rule.byMonthDay = formValues.byMonthDay;
  }

  if (endType === "on_date" && formValues.endDate) {
    rule.endDate = formValues.endDate;
  }

  if (endType === "after_occurrences" && formValues.maxOccurrences) {
    rule.maxOccurrences = Math.max(1, parseInt(formValues.maxOccurrences, 10));
    rule.occurrenceCount = formValues.occurrenceCount || 1;
  }

  rule.rawText = describeRecurrence(rule);
  return rule;
}

/**
 * Returns a human-readable description of a recurrence rule.
 * @param {Object} rule
 * @returns {string}
 */
export function describeRecurrence(rule) {
  if (!rule || !rule.isRecurring) return "";

  const prefix = rule.mode === "every!" ? "Every!" : "Every";
  const interval = rule.interval || 1;
  const freq = rule.frequency || "daily";

  const freqLabels = {
    daily: interval === 1 ? "day" : `${interval} days`,
    weekly: interval === 1 ? "week" : `${interval} weeks`,
    monthly: interval === 1 ? "month" : `${interval} months`,
    yearly: interval === 1 ? "year" : `${interval} years`,
  };

  let base = `${prefix} ${freqLabels[freq] || freq}`;

  if (freq === "weekly" && rule.byDay && rule.byDay.length > 0) {
    if (
      rule.byDay.length === 5 &&
      WEEKDAYS.every((d) => rule.byDay.includes(d))
    ) {
      base = `${prefix} weekday`;
    } else {
      const dayLabels = {
        MO: "Mon",
        TU: "Tue",
        WE: "Wed",
        TH: "Thu",
        FR: "Fri",
        SA: "Sat",
        SU: "Sun",
      };
      const names = rule.byDay.map((d) => dayLabels[d] || d).join(", ");
      base = `${prefix} ${interval > 1 ? `${interval} weeks on ` : ""}${names}`;
    }
  }

  if (freq === "monthly" && rule.byMonthDay && rule.byMonthDay.length > 0) {
    const ordinals = rule.byMonthDay.map((d) => ordinalSuffix(d)).join(", ");
    base = `${prefix} ${interval > 1 ? `${interval} months on the ` : ""}${ordinals}`;
  }

  const endType = rule.endType || (rule.endDate ? "on_date" : rule.maxOccurrences ? "after_occurrences" : "never");

  if (endType === "on_date" && rule.endDate) {
    base += ` until ${rule.endDate}`;
  } else if (endType === "after_occurrences" && rule.maxOccurrences) {
    base += `, for ${rule.maxOccurrences} occurrences`;
  }

  return base;
}

/**
 * Adds an ordinal suffix (st, nd, rd, th) to a number.
 * @param {number} n
 * @returns {string}
 */
function ordinalSuffix(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Quick presets mapping for the RecurrencePicker.
 */
export const RECURRENCE_PRESETS = [
  {
    label: "Every day",
    rule: { mode: "every", frequency: "daily", interval: 1 },
  },
  {
    label: "Every weekday",
    rule: {
      mode: "every",
      frequency: "weekly",
      interval: 1,
      byDay: WEEKDAYS,
    },
  },
  {
    label: "Every week",
    rule: { mode: "every", frequency: "weekly", interval: 1 },
  },
  {
    label: "Every month",
    rule: { mode: "every", frequency: "monthly", interval: 1 },
  },
  {
    label: "Every year",
    rule: { mode: "every", frequency: "yearly", interval: 1 },
  },
];

/**
 * Checks whether a task is configured as a repeatable task (active or closed/ended).
 * @param {Object} task - Task document data
 * @returns {boolean}
 */
export function isRepeatableTask(task) {
  if (!task) return false;
  const rec = task.recurrence;
  if (!rec) return false;
  return Boolean(
    rec.isRecurring === true ||
    rec.ended === true ||
    (rec.frequency && typeof rec.frequency === "string") ||
    rec.rawText
  );
}

/**
 * Checks whether a task is an active recurring task (scheduled to recur and not closed).
 * @param {Object} task - Task document data
 * @returns {boolean}
 */
export function isActiveRecurringTask(task) {
  if (!task) return false;
  return Boolean(task.recurrence?.isRecurring && task.status !== "Closed");
}

